import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { IApiError } from "app/bundles/requests/shared/models/apiError";
import { IApiErrorSuccessDTO } from "app/bundles/requests/shared/models/apiErrorSuccess";
import { RequestService } from "app/bundles/requests/shared/services/request.service";
import * as _ from "lodash-es";
import { CoreService } from "../core.service";
import { map, tap } from "rxjs";

type ErrorOrInfoType =
  | {
      error: { error: { error: IApiError }; status: number };
      type: "errorMessage";
    }
  | {
      error: { error: IApiErrorSuccessDTO; status: number };
      type: "infoMessage";
    }
  | {
      error: Error;
      type: "customError";
    };

declare let $: any;

@Component({
  selector: "app-error-message",
  templateUrl: "./error-message.component.html",
  styleUrls: ["./error-message.component.scss"],
})
export class ErrorMessageComponent implements AfterViewInit {
  @Input() title: string;
  @Input() showClose = true;
  @Input() enableModal = false;
  @Output() closeEvent: EventEmitter<any> = new EventEmitter();
  @Output() showModal = new EventEmitter<string>();
  modalEl = null;
  public error: { error: { error: IApiError }; status: number } = null;
  public errorMessage: string = null;
  public type: string = null;

  constructor(
    private _rootNode: ElementRef,
    private requestService: RequestService,
    private translateService: TranslateService,
    private coreService: CoreService,
  ) {}

  open({ error, type }: ErrorOrInfoType) {
    this.type = type;
    if (type === "errorMessage") {
      this.error = error;
    }
    if (type === "infoMessage") {
      this.error = {
        error: { error: error.error.error },
        status: error.status,
      };
    }
    if (type === "customError") {
      this.coreService.stopSpinner();
      this.errorMessage = error?.message || error.toString();
      this.modalEl.modal("show");
      return;
    }

    this.coreService.stopSpinner();

    const requestNumbers = this.extractRequestNumbers(this.error);
    const errorStatus = this.error?.status;
    const errorDetails = this.error?.error?.error?.details;
    const errorCodesDetail: {
      requestNumbers: string[];
      backendMessage: string;
      code: string;
    }[] = errorDetails ? this.groupByErrorCode(errorDetails) : [];

    const specificMessages =
      errorCodesDetail?.map?.(
        (d) => `components.errorMessageComponent.${errorStatus}.${d.code}`,
      ) || [];
    const genericFallbackMessage =
      "components.errorMessageComponent.errorMessage";

    this.translateService
      .stream([...specificMessages, genericFallbackMessage], {
        requestNumbers,
        errorStatus,
      })
      .pipe(
        map((maybeTranslatedMessages: { [k: string]: string }) => {
          const specificMessagesTranslated = specificMessages.map((sm) => {
            if (this.isMessageTranslated(maybeTranslatedMessages?.[sm])) {
              return maybeTranslatedMessages?.[sm];
            }
            // At this point, no translation available for this specificMessage
            // fr.json is missing this key. Bring attention to this.
            return `<p>Erreur lors de l'extraction du message pour ${sm}, Veuillez en informer le soutien informatique.</p><p>(status: ${errorStatus})</p>`;
          });
          // if no specific message apply (ex: error 5xx) give generic message
          this.errorMessage =
            specificMessagesTranslated?.join("") ||
            maybeTranslatedMessages?.[genericFallbackMessage];
        }),
        tap((_) => {
          this.modalEl.modal("show");
        }),
      )
      .subscribe();
  }

  private groupByErrorCode(
    details,
  ): { requestNumbers: string[]; backendMessage: string; code: string }[] {
    return (
      _.flow([
        (v) => _.groupBy(v, "code"),
        (v) =>
          _.map(v, (value, key) => ({
            code: key,
            backendMessage: value[0].message,
            requestNumbers: value.map((t) => t.target),
          })),
      ])(details) || []
    );
  }

  private isMessageTranslated(maybeTranslatedMessage: string) {
    return !maybeTranslatedMessage.includes("components.errorMessageComponent");
  }

  private extractRequestNumbers(error) {
    return error?.error?.error?.details
      ?.filter?.((r) => r?.target)
      ?.map?.((r) => r?.target)
      ?.join?.(", ");
  }

  onClose() {
    this.showModal.emit("false");
    this.modalEl.modal("hide");
    this.errorMessage = "";
    this.error = null;
    if (this.type !== "customError") {
      this.requestService.getGeojson();
    }
  }

  closeInternal() {
    this.onClose();
  }

  ngAfterViewInit() {
    this.modalEl = $(this._rootNode.nativeElement).find("div.modal");
  }

  has(selector) {
    return $(this._rootNode.nativeElement).find(selector).length;
  }
}
